/**
 * The audit log's plain words (docs/admin-area.md).
 *
 * `audit_log` keeps every change as a table name, an operation and the fields
 * that moved. This is the ONE place a row becomes a sentence a person reads
 * without a key: "Minty moved Sello Mabitsela to Dropout", "Lubabalo let
 * Siya open the admin area". Pure: no DOM, no Supabase, no clock. Tested in
 * test/admin-words.test.mjs with `npm test`.
 *
 * NEVER ECHO A RAW CODE AS A WORD. An unknown table or field is humanized
 * ("current training venue"), never printed as `current_training_venue_id`.
 */

export const KIND_LABELS = {
  access:   'Access',
  learners: 'Learners',
  classes:  'Classes',
  staff:    'Staff',
  pay:      'Pay',
};

export const KINDS = ['access', 'learners', 'classes', 'staff', 'pay'];

const TABLE_KIND = {
  user_roles: 'access',
  students: 'learners',
  student_class_enrollments: 'learners',
  classes: 'classes',
  employees: 'staff',
  employee_payslips: 'pay',
  employee_payslip_lines: 'pay',
  stipend_payment_status: 'pay',
  payslip_deliveries: 'pay',
  learner_doc_groups: 'pay',
  cohorts: 'pay',
  learner_doc_overrides: 'pay',
  learner_documents: 'pay',
  learner_payslips: 'pay',
};

export function kindOf(tableName) {
  return TABLE_KIND[tableName] ?? 'classes';
}

/** "current_training_venue_id" → "current training venue". */
export function humanize(code) {
  return String(code ?? '')
    .replace(/_id$/, '')
    .replace(/_uuid$/, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/** "dropout" → "Dropout". */
export function titleCase(code) {
  const h = humanize(code);
  return h ? h.charAt(0).toUpperCase() + h.slice(1) : '';
}

export function quoted(value, max = 60) {
  const v = String(value ?? '').trim();
  if (!v || v === 'null') return '';
  return `“${v.length > max ? v.slice(0, max - 1) + '…' : v}”`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function monthName(year, month) {
  const m = Number(month);
  const y = String(year ?? '').trim();
  if (!m || m < 1 || m > 12) return y;
  return `${MONTHS[m - 1]}${y ? ' ' + y : ''}`;
}

const ROLE_WORDS = { owner: 'Owner', admin: 'Admin', manager: 'Manager', facilitator: 'Facilitator', readonly: 'Read-only' };
export function roleName(code) {
  return ROLE_WORDS[String(code ?? '').trim()] ?? titleCase(code);
}

/** One part of the dashboard (access_parts.key), switched on or off for a person. */
const PART_WORDS = {
  learners:    { on: 'change learner records', off: 'changing learner records' },
  attendance:  { on: 'mark attendance', off: 'marking attendance' },
  setup:       { on: 'change classes and set-up', off: 'changing classes and set-up' },
  hr:          { on: 'see staff HR', off: 'seeing staff HR' },
  learner_pay: { on: 'see learner pay for every class', off: 'seeing learner pay for every class' },
};
function partWords(key) {
  return PART_WORDS[key] ?? { on: `use ${humanize(key)}`, off: `using ${humanize(key)}` };
}

/** The on/off columns user_roles had before the parts list (2026-10-20 → 27); old audit rows still carry them. */
const GRANT_WORDS = {
  can_open_admin:       { on: 'open the admin area', off: 'opening the admin area' },
  can_view_hr:          PART_WORDS.hr,
  can_view_learner_pay: PART_WORDS.learner_pay,
};

/** An audited text[] — '["hr", "learner_pay"]' or '{hr,learner_pay}' — to its keys. */
export function partList(value) {
  const v = String(value ?? '').trim();
  if (!v || v === 'null') return [];
  try {
    const arr = JSON.parse(v);
    if (Array.isArray(arr)) return arr.map(String);
  } catch { /* the Postgres array form */ }
  return v.replace(/^\{|\}$/g, '').split(',').map((s) => s.replace(/^"|"$/g, '').trim()).filter(Boolean);
}

/** Fields the log should not bother a reader with when they move alongside real ones. */
const QUIET_FIELDS = new Set(['updated_at', 'created_at', 'calculated_at', 'changed_at']);

/**
 * One audit entry as returned by list_audit_entries(), to a sentence.
 * @param {{table_name:string, operation:string, record?:string|null, record_extra?:any, changes?:Record<string,{old:any,new:any}>|null, status?:string|null, role?:string|null}} e
 * @returns {string} starting with a verb, e.g. "moved Sello Mabitsela to Dropout"
 */
export function describe(e) {
  const t = e.table_name;
  const op = e.operation;
  const who = e.record || null;
  const changes = Object.fromEntries(Object.entries(e.changes ?? {}).filter(([k]) => !QUIET_FIELDS.has(k)));
  const keys = Object.keys(changes);

  switch (t) {
    case 'user_roles': {
      const target = who ?? 'someone';
      if (op === 'INSERT') return e.role ? `gave ${target} access as ${roleName(e.role)}` : `gave ${target} access`;
      if (op === 'DELETE') return `removed ${target}’s access`;
      const parts = [];
      if (changes.role) parts.push(`changed ${target}’s role from ${roleName(changes.role.old)} to ${roleName(changes.role.new)}`);
      for (const [k, w] of Object.entries(GRANT_WORDS)) {
        if (changes[k]) parts.push(String(changes[k].new) === 'true' ? `let ${target} ${w.on}` : `stopped ${target} ${w.off}`);
      }
      if (changes.parts) {
        const before = partList(changes.parts.old);
        const after = partList(changes.parts.new);
        for (const k of after.filter((p) => !before.includes(p))) parts.push(`let ${target} ${partWords(k).on}`);
        for (const k of before.filter((p) => !after.includes(p))) parts.push(`stopped ${target} ${partWords(k).off}`);
      }
      if (changes.facilitator_id) parts.push(`linked ${target} to a facilitator record`);
      return parts.length ? parts.join(' and ') : `changed ${target}’s access`;
    }
    case 'students': {
      const name = who ?? 'a learner';
      if (op === 'INSERT') return `added ${name} as a learner`;
      if (op === 'DELETE') return `removed the learner ${name}`;
      if (changes.status) return `moved ${name} to ${titleCase(changes.status.new)}${changes.status.old ? ` (was ${titleCase(changes.status.old)})` : ''}`;
      return `updated ${name}’s ${fieldList(keys)}`;
    }
    case 'student_class_enrollments': {
      const name = who ?? 'a learner';
      const cls = e.record_extra?.class ? ` in ${e.record_extra.class}` : '';
      if (op === 'INSERT') return `enrolled ${name}${cls}`;
      if (op === 'DELETE') return `removed ${name}’s enrolment${cls}`;
      if (changes.learning_stage) return `moved ${name} to the ${titleCase(changes.learning_stage.new)} stage${cls}`;
      if (changes.status) return `set ${name}’s enrolment${cls} to ${titleCase(changes.status.new)}`;
      return `updated ${name}’s enrolment${cls} (${fieldList(keys)})`;
    }
    case 'classes': {
      const name = who ? `the class ${quoted(who)}` : 'a class';
      if (op === 'INSERT') return `created ${name}`;
      if (op === 'DELETE') return `deleted ${name}`;
      if (changes.status) return `set ${name} to ${titleCase(changes.status.new)}`;
      if (changes.facilitator_id) return `reassigned ${name} to another facilitator`;
      if (changes.attendance_autogen_enabled) return String(changes.attendance_autogen_enabled.new) === 'true' ? `switched daily attendance on for ${name}` : `switched daily attendance off for ${name}`;
      return `updated ${name} (${fieldList(keys)})`;
    }
    case 'employees': {
      const name = who ?? 'a staff member';
      if (op === 'INSERT') return `added ${name} to the staff register`;
      if (op === 'DELETE') return `removed ${name} from the staff register`;
      if (changes.is_active) return String(changes.is_active.new) === 'true' ? `reactivated ${name}` : `deactivated ${name}`;
      if (changes.contracted_net_salary) return `changed ${name}’s pay`;
      return `updated ${name}’s ${fieldList(keys)}`;
    }
    case 'employee_payslips': {
      const name = who ?? 'a staff member';
      const period = e.record_extra ? monthName(e.record_extra.year, e.record_extra.month) : '';
      const slip = `${name}’s ${period ? period + ' ' : ''}payslip`;
      if (op === 'INSERT') return `created ${slip}`;
      if (op === 'DELETE') return `deleted ${slip}`;
      if (changes.status) return `marked ${slip} ${titleCase(changes.status.new)}`;
      if (keys.length === 0) return `recalculated ${slip}`;
      return `changed ${slip} (${fieldList(keys)})`;
    }
    case 'employee_payslip_lines': {
      if (op === 'INSERT') return `added a line to a staff payslip`;
      if (op === 'DELETE') return `removed a line from a staff payslip`;
      return `changed a line on a staff payslip`;
    }
    case 'stipend_payment_status': {
      const name = who ?? 'a learner';
      const period = e.record_extra ? monthName(e.record_extra.year, e.record_extra.month) : '';
      const stip = `${name}’s ${period ? period + ' ' : ''}stipend`;
      if (op === 'DELETE') return `cleared the payment status on ${stip}`;
      const status = changes.status?.new ?? e.status;
      return status ? `marked ${stip} ${titleCase(status)}` : `updated ${stip}`;
    }
    case 'payslip_deliveries': {
      const name = who ?? 'a learner';
      if (op === 'INSERT') return `sent ${name}’s payslip by WhatsApp`;
      if (op === 'DELETE') return `cleared ${name}’s WhatsApp payslip record`;
      if (changes.status) return `WhatsApp payslip for ${name}: ${humanize(changes.status.new)}`;
      return `updated ${name}’s WhatsApp payslip record`;
    }
    default: {
      const thing = `a ${humanize(t)} record`;
      if (op === 'INSERT') return `added ${thing}`;
      if (op === 'DELETE') return `removed ${thing}`;
      return `updated ${thing}${keys.length ? ` (${fieldList(keys)})` : ''}`;
    }
  }
}

/** ["first_name","phone_number"] → "first name and phone number"; many → "first name, phone number and 3 more". */
export function fieldList(keys) {
  const words = keys.map(humanize).filter(Boolean);
  if (words.length === 0) return 'details';
  if (words.length === 1) return words[0];
  if (words.length === 2) return `${words[0]} and ${words[1]}`;
  if (words.length <= 4) return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `${words.slice(0, 2).join(', ')} and ${words.length - 2} more`;
}

/** Who did it, as a name. Direct SQL and cron changes have no actor. */
export function whoName(e) {
  if (e.who_name) return e.who_name;
  if (!e.who_email) return 'The system';
  return String(e.who_email).split('@')[0];
}

/** Newest first, ties broken by id so the order is stable across reloads. */
export function newestFirst(a, b) {
  if (a.at !== b.at) return a.at < b.at ? 1 : -1;
  return Number(a.id) < Number(b.id) ? 1 : -1;
}
